/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import MyApp from './myApp.tsx';
import { reportClientError } from './errorReport.ts';
import { ensureLocale, getPersistedLanguage } from './i18n/i18n.ts';
import { isReloadingForNewBuild, isStaleChunkError, reloadForNewBuild } from './staleBuild.ts';
// import reportWebVitals from './reportWebVitals';

// React error boundaries (see ErrorPage) only catch exceptions thrown during
// render/lifecycle. Errors from async code, event handlers, timers, and the
// requestAnimationFrame simulation loop escape them — with no handler they used
// to vanish silently. Log them so a captured stack survives for diagnosis.
window.addEventListener('error', (e) => {
  // A stale-build chunk failure is handled by the reload below, not a bug report.
  if (isStaleChunkError(e.error?.message ?? String(e.message ?? '')) || isReloadingForNewBuild()) return;
  console.error('AIMS uncaught error:', e.error ?? e.message, e);
  reportClientError({
    source: 'window.onerror',
    message: e.error?.message ?? String(e.message ?? ''),
    stack: e.error?.stack,
  });
});
window.addEventListener('unhandledrejection', (e) => {
  const r = e.reason as { message?: string } | undefined;
  if (isStaleChunkError(r?.message ?? String(e.reason ?? '')) || isReloadingForNewBuild()) return;
  console.error('AIMS unhandled promise rejection:', e.reason, e);
  const reason = e.reason;
  reportClientError({
    source: 'unhandledrejection',
    message: reason?.message ?? String(reason ?? ''),
    stack: reason?.stack,
  });
});

// Vite fires this when a code-split chunk fails to load — in production almost
// always a tab that outlived a deploy (the old chunk files are gone). Reload
// onto the new build (staleBuild.ts). NOT preventDefault: Vite then swallows
// the error and the import resolves to undefined, which React.lazy turns into a
// different, unrecognisable crash. Let it throw; the error page sees the reload
// already under way and shows the update note instead of an error.
window.addEventListener('vite:preloadError', () => {
  reloadForNewBuild();
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// Non-English locales are code-split, so the bundle for a returning Chinese
// user has to be registered before the first render or the whole app would
// paint in English and then swap. ensureLocale resolves immediately for 'en'
// (the overwhelmingly common case), so this costs English users nothing beyond
// an already-resolved promise. A failed locale fetch logs and falls back to
// English rather than blocking the boot — see ensureLocale.
ensureLocale(getPersistedLanguage()).then(() => {
  root.render(
    <React.StrictMode>
      <MyApp />
    </React.StrictMode>,
  );
});

// Dev-only: register window.runRoutingEvalLive() so the agent-routing corpus can
// be scored against the live interpreter from the browser console. Dynamically
// imported under DEV so it is tree-shaken out of production builds.
if (import.meta.env.DEV) {
  import('./agent/routingEvalLive.ts');
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */
